/**
 * **What an attack consumes** — the two `resolveSingleTarget` stages that spend something, and
 * the per-clause step builders they use.
 *
 * - **On FIRE, unconditional on hit or miss** ({@link foldWeaponFireBookkeeping}, S8): the
 *   Recharge discharge, Loading and Limited costs (weapon cost events, settled at the action's end,
 *   `events/gameEvents.ts`), and the Heat (Self) amount. Raised once per fire, never once per
 *   caught target.
 * - **Before the roll, at the wielder's option** ({@link settleConsumeChoice}): the Lock On "*may*
 *   consume" prompt round-trip. Its answer decides whether Lock On's +1 (and anything gated on
 *   `match.consumes`) applies to this attack; {@link consumeStatusCosts} then clears the chosen
 *   statuses off the TARGET once the attack resolves, hit or miss (S10).
 *
 * Only the consume choice can halt the resolution, and it does so before any dice are drawn.
 */

import type { Cell, Combatant, EncounterState, ResolvedStep, WeaponProfile } from "@lancer-vtt/domain";
import { encounterGridMode, hasStatus, tagCell, movementContext, sideOf, threatValue, thrownWeaponObjectId, weaponInstanceKey, worldFor } from "@lancer-vtt/domain";
import { combatantRange, footprintCells } from "../effects/spatialAnswerer";
import { spawnObject } from "../spawn/object";
import type { NeedsDecision } from "./types";
import type { ConsumeStatusEvent, WeaponCostEvent } from "../events/gameEvents";

/**
 * What firing `weapon` spends, unconditional on hit or miss, in the order the spine always spent
 * it: Recharge discharge (RAW Core p.281), the Loading unload, one Limited charge. Each is a weapon
 * cost event on the weapon's instance key; settlement skips one already spent.
 */
export function weaponFireCosts(carrier: string, weapon: WeaponProfile): WeaponCostEvent[] {
  const key = weaponInstanceKey(weapon);
  return [
    ...(weapon.recharge ? [{ kind: "weapon" as const, carrier, key, spend: "discharge" as const }] : []),
    ...(weapon.loading ? [{ kind: "weapon" as const, carrier, key, spend: "loading" as const }] : []),
    ...(weapon.limited !== undefined ? [{ kind: "weapon" as const, carrier, key, spend: "limited" as const, limit: weapon.limited }] : []),
  ];
}

/** What {@link foldWeaponFireBookkeeping} settles: the costs this fire owes, and the self-heat. */
export type WeaponFireBookkeeping = {
  /** The Recharge discharge, Loading and Limited costs, for the caller to raise on its ledger. */
  costs: WeaponCostEvent[];
  /**
   * The Heat (Self) amount this fire owes its wielder — **not** a step. On the hit arm it is folded
   * into the SAME single `resolveHeatCascade` call as Overkill's heat, inside
   * {@link import("./damageApply").applyHitDamage}; the Reliable-floor arm
   * ({@link import("./damageApply").applyReliableFloorDamage}) and `resolveSingleTarget`'s own
   * plain-miss arm each spend it on their own. Either way this stage returns the amount and never
   * emits the cascade itself.
   */
  selfHeatAmt: number;
};

/**
 * **S8 — the weapon-fire bookkeeping.** The eighth stage of `attack.ts`'s `resolveSingleTarget`
 * pipeline: everything a fire spends *unconditionally on hit or miss*, gathered in the one order
 * the spine emitted them — Recharge discharge, then Loading, then Limited, then the Heat (Self)
 * amount.
 *
 * Not pure, but close: it reads `attacker.current` (the affordance-state ledger and the
 * weapon-resource bag) and the selected firing profile, and it carries **no exit** — no dice, no decision, no
 * mutation of anything the caller owns. It returns its steps rather than pushing onto the
 * caller's accumulator.
 *
 * **`sharedDamage` skips all four**, and that is the whole reason this stage takes the flag: an
 * area fan-out's per-target sub-resolution must NOT spend the fire again. The weapon fired ONCE
 * regardless of how many targets its template caught, so `resolveAreaAttack` does this
 * bookkeeping itself, once, after its loop. **That once-per-attack copy is deliberately NOT this
 * function**: its gating differs (it runs unconditionally rather than under `!sharedDamage`), it
 * folds its self-heat into its own cascade call alongside the shared pool's Overkill heat, and
 * `resolve-single-target-decomposition`'s brief rules the two out of unification explicitly. Do
 * not "dedupe" them.
 *
 * The three costs are {@link weaponFireCosts}; their grounding is the weapon cost event's
 * (`events/gameEvents.ts`).
 */
export function foldWeaponFireBookkeeping(opts: {
  attacker: Combatant;
  weapon: WeaponProfile;
  /** True when this is an area fan-out's per-target sub-resolution (the spine's `sharedDamage`). */
  sharedDamage: boolean;
  /**
   * `false` for a forced attack that spends nothing (Rebound's reflection, `npc-features-fielded-
   * roster` Phase 11.1): no Recharge discharge, Loading or Limited consume. Heat (Self) still lands.
   */
  consume: boolean;
  /** `true` for a weapon's own secondary attack (the Annihilator's): no Heat (Self). */
  secondary?: boolean;
}): WeaponFireBookkeeping {
  const { attacker, weapon, sharedDamage, consume, secondary = false } = opts;
  // The three consumes below are skipped by either switch; only `sharedDamage` zeroes Heat (Self).
  const skipConsume = sharedDamage || !consume;
  const costs = skipConsume ? [] : weaponFireCosts(attacker.id, weapon);

  // Heat (Self) (`tg_heat_self {N}`, RAW Core pp.104–106; verified `raw-fidelity-sweep`
  // item 4): a weapon carrying this tag deals N heat to its OWN wielder every time it
  // is FIRED — **unconditional on hit or miss** (distinct from Overkill's reroll-driven
  // heat, which only fires on a hit that rolls damage). Folded into the SAME single
  // `resolveHeatCascade` call as Overkill's heat in the caller (never a SECOND independent
  // call) — `resolveHeatCascade` reads the wielder's *pre-resolution* heat off the
  // frozen `attacker` snapshot, so two separate calls in one resolution would each
  // recompute the cap-cross from the SAME starting heat and silently lose whichever
  // amount was added first when the combined total is what actually crosses the cap.
  // **Skipped (stays 0) when `sharedDamage` is set** (an AoE fan-out's per-target
  // sub-resolution, `resolveAreaAttack`'s inner loop) — the weapon fired ONCE
  // regardless of how many targets its template catches, so self-heat is folded into
  // `resolveAreaAttack`'s own once-per-attack cascade call after the loop instead,
  // mirroring exactly how the shared-pool Overkill heat there is computed once, not
  // per target. A weapon with no `tg_heat_self` tag (the common case — including every
  // synthetic melee-action/tech-attack profile, which carry no tags at all) contributes 0.
  const selfHeatAmt = !sharedDamage && !secondary && weapon.selfHeat ? weapon.selfHeat : 0;

  return { costs, selfHeatAmt };
}

/** What {@link settleConsumeChoice} settles: which consumable statuses this attack spends. */
export type ConsumeChoice = {
  /** Statuses the wielder consumes: their modifiers apply, and they clear after the attack. */
  consume: string[];
  /** Statuses the wielder keeps: their modifiers are dropped from this attack and they stay. */
  keep: string[];
};

/**
 * **The Lock On "*may* consume" choice, settled before the roll.** RAW (Core p.70, Lock On): "Any
 * character making an attack against a character with LOCK ON may choose to gain +1 on that
 * attack and then clear the LOCK ON condition after that attack resolves." The +1 and the clear
 * are one choice, made as the attack is declared, so the answer gates the S3 fold: a kept status's
 * `consumeOnHit` modifier is dropped (no +1), and a consumed one reaches `match.consumes` (Spotter
 * 1's roll-twice, Hacker 1's heat-or-push). The clear lands after the attack resolves, hit or
 * miss ({@link consumeStatusCosts}).
 *
 * It asked after a hit until `pc-content-sweep` (2026-09-25): the +1 was already folded by then,
 * so "keep" kept the status AND the +1, and a keep answer never reached the talents gated on
 * consuming.
 *
 *   - a recorded `intent.choices["consume:<status>"]` ("consume" | "keep") is honored;
 *   - in interactive mode an unanswered choice returns `needs_decision` (the prompt);
 *   - non-interactive (replay / CLI default) consumes.
 *
 * The prompt is routed to `attacker.id`: the WIELDER decides whether to spend the Lock On.
 */
export function settleConsumeChoice(opts: {
  attacker: Combatant;
  /** The fold's `consumeOnHit` facet: the consumable statuses the target carries. */
  consumable: string[];
  /** The intent's recorded choices — where an already-answered prompt is read from. */
  choices: Record<string, string> | undefined;
  /** `effects.interactive` — whether an unanswered choice halts for a prompt or takes the default. */
  interactive: boolean | undefined;
}): ConsumeChoice | NeedsDecision {
  const { attacker, consumable, choices, interactive } = opts;
  const out: ConsumeChoice = { consume: [], keep: [] };
  for (const status of new Set<string>(consumable)) {
    const promptId = `consume:${status}`;
    const recorded = choices?.[promptId];
    if (recorded === "consume" || recorded === "keep") {
      out[recorded].push(status);
    } else if (interactive) {
      return {
        kind: "needs_decision",
        who: attacker.id,
        promptId,
        prompt: `Consume ${status} on this attack? Consuming grants its +1 Accuracy and clears it after the attack, hit or miss; keeping forgoes the +1 and anything that needs ${status} consumed.`,
        options: [
          { id: "consume", label: `Consume ${status} (+1 Accuracy)` },
          { id: "keep", label: `Keep ${status} (no +1)` },
        ],
      };
    } else {
      out.consume.push(status); // deterministic default (replay / CLI).
    }
  }
  return out;
}

/**
 * **S10 — clear the consumed statuses** off the TARGET once the attack has resolved, hit or miss
 * (RAW: "clear the LOCK ON condition after that attack resolves"). One `consume_status` cost per
 * status {@link settleConsumeChoice} consumed, settled where it stands.
 */
export function consumeStatusCosts(target: Combatant, consumed: readonly string[]): ConsumeStatusEvent[] {
  return consumed.map((status) => ({ kind: "consume_status", carrier: target.id, status }));
}

/**
 * **A throw lands its weapon downrange** (`tg_thrown`, Core p.105: "a thrown weapon comes to rest in
 * an adjacent space to its target and must be retrieved as a free action while adjacent to that
 * weapon before it can be used again"). An attack with a Thrown weapon at a target beyond its
 * Threat is a throw: it spawns a Size 1/2 object, {@link thrownWeaponObjectId}, on the free space
 * beside the target where the throw's straight line arrives (else the free space beside it nearest
 * the thrower, ties by cell key), on the thrower's side and deployed
 * from `tg_thrown`, so its owner's `tg_thrown#Retrieve` clears it. `checkLoaded` refuses the weapon
 * while it stands. Hit or miss alike. No free adjacent space: nothing lands, with a warning, and
 * the weapon stays in hand. A swing within Threat is a melee attack, not a throw.
 */
export function thrownLandingSteps(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile }): { steps: ResolvedStep[]; warnings: string[] } {
  const { state, attacker, target, weapon } = args;
  if (!weapon.tags?.some((t) => t.id === "tg_thrown") || target.pos == null) return { steps: [], warnings: [] };
  const distance = combatantRange(state, attacker.id, target.id);
  if (distance === null || distance <= threatValue(weapon.range ?? [])) return { steps: [], warnings: [] };
  const world = worldFor(state);
  const ctx = movementContext(world, state, "");
  const occupied = new Set(Object.values(state.combatants).filter((c) => !hasStatus(c.statuses, "Destroyed")).flatMap((c) => footprintCells(world, c).map((f) => world.key(f))));
  const targetCells = footprintCells(world, target);
  const attackerCells = footprintCells(world, attacker);
  const candidates: Cell[] = [];
  for (const t of targetCells) {
    for (const n of world.neighbors(t)) {
      const key = world.key(n);
      if (!ctx.inBounds(n) || ctx.blocked.has(key) || occupied.has(key) || candidates.some((c) => world.key(c) === key)) continue;
      candidates.push(n);
    }
  }
  // The space the throw's straight line crosses last before the target, when free; else the free
  // space nearest the thrower (ties by cell key).
  const targetKeys = new Set(targetCells.map((c) => world.key(c)));
  const onLine = world.line(attacker.pos!, target.pos).filter((c) => !targetKeys.has(world.key(c))).at(-1);
  const nearest = (c: Cell) => Math.min(...attackerCells.map((a) => world.distance(a, c)));
  const landing = candidates.find((c) => onLine !== undefined && world.equals(c, onLine)) ?? candidates.sort((a, b) => nearest(a) - nearest(b) || world.key(a).localeCompare(world.key(b)))[0];
  if (landing === undefined) return { steps: [], warnings: [`${weapon.name}: no free space beside ${target.name} for the thrown weapon to land in — it stays in ${attacker.name}'s hand.`] };
  const spawned = spawnObject({ combatantId: thrownWeaponObjectId(attacker.id, weaponInstanceKey(weapon)), name: `${weapon.name} (thrown)`, size: 0.5, allegiance: sideOf(attacker) });
  if (!spawned.ok) return { steps: [], warnings: [`${weapon.name}: the thrown weapon could not be placed (${spawned.error}).`] };
  const combatant: Combatant = { ...spawned.value, pos: tagCell(encounterGridMode(state), landing), deployedFrom: { combatantId: attacker.id, entryId: "tg_thrown" } };
  return { steps: [{ op: "spawn_object", combatant }], warnings: [`${weapon.name} lands beside ${target.name}; ${attacker.name} must retrieve it before using it again.`] };
}
